import { Button, message, Spin } from 'antd'
import { PDFDocument } from 'pdf-lib'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import {
  getContract,
  getContractForView,
  signContract,
} from '../../../../api/aopsHR.api'
import { salusS3 } from '../../../../api/s3.api'
import AopsCommonModalLayout from '../../../../components/apos/modal/AopsCommonModalLayout'
import { userState } from '../../../../store/user'
import '../../orgChart/orgChart.scss'
import { workTypeMap } from '../../orgChart/utils/employmentMappings'
import { generateContractPdf } from '../../orgChart/utils/generateContractPdf'
import DocumentViewer from './DocumentViewer'
import SignatureModal from './SignatureCanvas'

const ContractSignModal = ({
  open,
  onClose,
  contract,
  userId,
  onSignComplete,
  mode = 'sign', // 'sign' | 'view'
  isChief = false,
}) => {
  const [showSignatureModal, setShowSignatureModal] = useState(false)
  const [signatureImage, setSignatureImage] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [originalPdfUrl, setOriginalPdfUrl] = useState(null)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [user] = useRecoilState(userState)

  // PDF 생성: contract 데이터 받아서 생성 (없으면 빈 값으로)
  useEffect(() => {
    if (open) {
      const generatePdf = async () => {
        setIsGeneratingPdf(true)
        setPdfUrl(null) // 이전 URL 초기화
        setOriginalPdfUrl(null) // 원본 URL 초기화
        setSignatureImage(null) // 서명 초기화

        try {
          // 계약서 단건 조회 API 호출
          let contractSource = contract || {}
          let snapshot = contractSource?.snapshot || {}

          if (contract?.id) {
            try {
              // 문서 보기 모드이거나 완료 상태일 때는 getContractForView 사용 (userId 불필요)
              // 서명 모드일 때는 getContract 사용 (userId 필요하지만 prop이 없으면 userState에서 가져옴)
              let res = null
              const effectiveUserId = userId || user?.id

              if (mode === 'view' || contract?.status === '완료') {
                // 문서 보기 모드: getContractForView 사용 (관리자, 계약자 모두 가능)
                res = await getContractForView(contract.id)
                // console.log('getContractForView res::', res)
              } else {
                // 서명 모드: getContract 사용 (userId 필요)
                if (!effectiveUserId) {
                  message.error('사용자 정보를 찾을 수 없습니다.')
                  setIsGeneratingPdf(false)
                  return
                }
                res = await getContract(contract.id, {
                  userId: effectiveUserId,
                })
                console.log('getContract res::', res)
              }

              // API 응답이 없으면 에러 처리
              if (!res || !res.data) {
                message.error('계약서 정보를 불러올 수 없습니다.')
                setIsGeneratingPdf(false)
                return
              }

              // console.log(
              //   mode === 'view' || contract?.status === '완료'
              //     ? 'getContractForView res::'
              //     : 'getContract res::',
              //   res,
              // )
              const detail = res.data || {}
              const responseSnapshot = detail?.snapshot || {}

              // 상태가 COMPLETED이고 snapshot.pdfUrl이 있으면 새 창으로 열기
              if (
                (mode === 'view' ||
                  contract?.status === '완료' ||
                  detail.status === 'COMPLETED') &&
                responseSnapshot.pdfUrl
              ) {
                let pdfUrlToOpen = responseSnapshot.pdfUrl

                // pdfUrl이 S3 key인 경우 (http로 시작하지 않으면) 전체 URL로 변환
                if (pdfUrlToOpen && !pdfUrlToOpen.startsWith('http')) {
                  pdfUrlToOpen = `${process.env.REACT_APP_S3_HOST}/${pdfUrlToOpen}.pdf`
                }

                console.log('Opening PDF in new window::', pdfUrlToOpen)
                window.open(pdfUrlToOpen, '_blank')
                setIsGeneratingPdf(false)
                onClose() // 모달 닫기
                return
              }

              // 문서 보기 모드에서 서명된 PDF URL이 있으면 바로 표시
              if (
                (mode === 'view' || contract?.status === '완료') &&
                (detail.signedPdfUrl || detail.pdfUrl)
              ) {
                let pdfUrlToShow = detail.signedPdfUrl || detail.pdfUrl

                // pdfUrl이 S3 key인 경우 (http로 시작하지 않으면) 전체 URL로 변환
                if (pdfUrlToShow && !pdfUrlToShow.startsWith('http')) {
                  pdfUrlToShow = `${process.env.REACT_APP_S3_HOST}/${pdfUrlToShow}.pdf`
                }

                console.log('pdfUrlToShow::', pdfUrlToShow)

                setPdfUrl(pdfUrlToShow)
                setIsGeneratingPdf(false)
                return
              }

              // contractSource 업데이트
              contractSource = {
                ...contract,
                ...detail,
              }
              snapshot = contractSource?.snapshot || {}

              // console.log('contractSource::', contractSource)
            } catch (error) {
              console.error('계약서 조회 오류:', error)
              message.error('계약서 조회 중 오류가 발생했습니다.')
            }
          }

          // 시간 파싱 유틸 (예: "09시 30분" -> { hour: '09', minute: '30' })
          const parseTimeText = (
            text,
            defaultHour = '09',
            defaultMinute = '00',
          ) => {
            if (!text || typeof text !== 'string') {
              return { hour: defaultHour, minute: defaultMinute }
            }
            const match = text.match(/(\d{1,2})\s*시\s*(\d{1,2})\s*분/)
            if (!match) {
              return { hour: defaultHour, minute: defaultMinute }
            }
            const [, h, m] = match
            const hour = String(h).padStart(2, '0')
            const minute = String(m).padStart(2, '0')
            return { hour, minute }
          }

          // 휴게시간 파싱 유틸 (예: "휴게시간 : 12시 30분 ~ 13시 30분")
          const parseBreakTimeText = (text) => {
            if (!text || typeof text !== 'string') {
              return {
                startHour: '12',
                startMinute: '30',
                endHour: '13',
                endMinute: '30',
              }
            }
            const matches = [
              ...text.matchAll(/(\d{1,2})\s*시\s*(\d{1,2})\s*분/g),
            ]
            if (matches.length < 2) {
              return {
                startHour: '12',
                startMinute: '30',
                endHour: '13',
                endMinute: '30',
              }
            }
            const [start, end] = matches
            const startHour = String(start[1]).padStart(2, '0')
            const startMinute = String(start[2]).padStart(2, '0')
            const endHour = String(end[1]).padStart(2, '0')
            const endMinute = String(end[2]).padStart(2, '0')
            return { startHour, startMinute, endHour, endMinute }
          }

          const startTimeFromSnapshot = parseTimeText(snapshot.workStartTime)
          const endTimeFromSnapshot = parseTimeText(snapshot.workEndTime)
          const breakTimeFromSnapshot = parseBreakTimeText(snapshot.breakTime)

          // contract와 snapshot에서 받은 데이터로 PDF 생성
          // workType을 한글로 변환
          const workTypeDisplay =
            workTypeMap[snapshot.workType] || snapshot.workType || '정규직'

          // console.log('contractSource?::', contractSource)

          const contractData = {
            issueDate: new Date(),
            certificateNo: contractSource?.contractNo || '',
            contractStartDate: snapshot.joinDate || null,
            contractEndDate: contractSource?.contractEndDate || null,
            // contractType이 EMPLOYMENT일 때 snapshot.workType을 사용 (getContractTitle, getContractPeriodSection용)
            contractType: contractSource?.contractType,
            workType: snapshot.workType, // 근로계약서 일 때
            // 소속/직무/직급은 snapshot 값을 사용
            department: snapshot.department || '',
            position: snapshot.position || '', // 직무
            rank: snapshot.rank || '', // 직급
            workTypeDisplay: workTypeDisplay, // 근무형태는 한글로 변환
            studioName: contractSource?.studioName || '',
            teamName: contractSource?.teamName || '',
            // 근무/휴게 시간은 snapshot 텍스트를 파싱한 값 그대로 사용
            workStartTime: startTimeFromSnapshot.hour,
            workStartMinute: startTimeFromSnapshot.minute,
            workEndTime: endTimeFromSnapshot.hour,
            workEndMinute: endTimeFromSnapshot.minute,
            breakStartTime: breakTimeFromSnapshot.startHour,
            breakStartMinute: breakTimeFromSnapshot.startMinute,
            breakEndTime: breakTimeFromSnapshot.endHour,
            breakEndMinute: breakTimeFromSnapshot.endMinute,
            // 근무 장소 역시 snapshot 값 그대로
            workplace: snapshot.workLocation,
            workdays: contractSource?.workdays || '매주 5일 근무',
            holidays: contractSource?.holidays || '주휴일 및 법정공휴일',
            // 급여는 snapshot의 baseSalary/annualSalary를 그대로 사용
            totalSalary: snapshot.annualSalary,
            basicSalary: snapshot.baseSalary,
            fixedAllowance: contractSource?.fixedAllowance || '',
            incentive: contractSource?.incentive || '',
            paymentTime: contractSource?.paymentTime || '매월',
            paymentCycle: contractSource?.paymentCycle || '매월',
            paymentMethod: contractSource?.paymentMethod || '',
            paymentStandard: contractSource?.paymentStandard || '',
            hasBonus: contractSource?.hasBonus || false,
            bonusStandard: contractSource?.bonusStandard || '',
            adjustmentTime: contractSource?.adjustmentTime || '매년 1회',
            adjustmentStandard:
              contractSource?.adjustmentStandard ||
              '개인 성과 및 회사 정책에 따라',
            employmentInsurance: contractSource?.employmentInsurance ?? true,
            industrialAccidentInsurance:
              contractSource?.industrialAccidentInsurance ?? true,
            nationalPension: contractSource?.nationalPension ?? true,
            healthInsurance: contractSource?.healthInsurance ?? true,
            contractDate: contractSource?.contractDate || new Date(),
            // 직원 인적사항은 snapshot 구조 그대로 사용
            employeeName: snapshot.name,
            employeeAddress: snapshot.address,
            employeePhone: snapshot.contact,
            employeeEmail: snapshot.email,
            // 주민등록번호는 snapshot.employeeRRN 그대로 사용
            employeeBirthDate: snapshot.employeeRRN,
            employeeRRN: snapshot.employeeRRN,
          }

          // contractType에 따라 템플릿 결정 (SALARY: 연봉계약서, EMPLOYMENT: 근로계약서)
          const contractType =
            contractSource?.contractType || contract?.contractType
          const templateType = contractType === 'SALARY' ? 'salary' : 'labor'

          const pdfBlob = await generateContractPdf(contractData, templateType)
          if (pdfBlob instanceof Blob) {
            // Blob에서 URL 생성하여 뷰어에 표시
            const blobUrl = URL.createObjectURL(pdfBlob)
            setPdfUrl(blobUrl)
            setOriginalPdfUrl(blobUrl) // 원본 PDF URL 저장
          } else {
            throw new Error('PDF Blob이 생성되지 않았습니다.')
          }
        } catch (error) {
          message.error('PDF 생성 중 오류가 발생했습니다.')
          setPdfUrl(null)
        } finally {
          setIsGeneratingPdf(false)
        }
      }

      generatePdf()
    }

    // 모달이 닫힐 때 정리
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
      if (originalPdfUrl) {
        URL.revokeObjectURL(originalPdfUrl)
      }
      setPdfUrl(null)
      setOriginalPdfUrl(null)
      setSignatureImage(null)
    }
  }, [open, contract, mode, userId])

  // pdfUrl이 변경될 때 이전 URL 정리
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [pdfUrl])

  const handleGoToSign = () => {
    // PDF가 없거나 생성 중이면 서명 모달을 열지 않음
    if (!pdfUrl || isGeneratingPdf) {
      message.warning('PDF가 준비되지 않았습니다. 잠시만 기다려주세요.')
      return
    }

    // 서명이 있으면 제거하고 원본 PDF로 되돌리기
    if (signatureImage) {
      setSignatureImage(null)
      // 원본 PDF로 되돌리기
      if (originalPdfUrl) {
        if (pdfUrl && pdfUrl !== originalPdfUrl) {
          URL.revokeObjectURL(pdfUrl)
        }
        setPdfUrl(originalPdfUrl)
      }
    }
    setShowSignatureModal(true)
  }

  const handleSignatureSave = async (signatureDataURL) => {
    if (!pdfUrl) {
      message.error('PDF가 없습니다.')
      return
    }

    try {
      setIsProcessing(true)

      // 1. 현재 뷰어에 표시된 PDF 파일 가져오기
      const pdfBytes = await fetch(pdfUrl).then((res) => {
        if (!res.ok) {
          throw new Error('PDF를 불러올 수 없습니다.')
        }
        return res.arrayBuffer()
      })

      // 2. PDF 로드
      const pdfDoc = await PDFDocument.load(pdfBytes)

      // 3. 서명 이미지를 PDF에 삽입 (마지막 페이지 기준, 2장 이상이면 두 번째 페이지 우선)
      const pages = pdfDoc.getPages()
      const targetPage = pages.length >= 2 ? pages[1] : pages[pages.length - 1]

      // 서명 이미지 가져오기 (Data URL에서)
      const signatureImageBytes = await fetch(signatureDataURL).then((res) =>
        res.arrayBuffer(),
      )
      const signatureImageEmbed = await pdfDoc.embedPng(signatureImageBytes)

      // 서명 위치 설정 (근로/연봉 계약서에 따라 Y 좌표 다르게)
      // contractType에 따라 결정 (SALARY: 연봉계약서, EMPLOYMENT: 근로계약서)
      const contractType = contract?.contractType
      const isSalaryContract = contractType === 'SALARY'
      const signatureWidth = 100
      const signatureHeight = 60

      const x = isSalaryContract ? 70 : 80
      const y = isSalaryContract ? 140 : 340

      targetPage.drawImage(signatureImageEmbed, {
        x: x,
        y: y,
        width: signatureWidth,
        height: signatureHeight,
      })

      // 4. 서명이 추가된 PDF 저장
      const signedPdfBytes = await pdfDoc.save()

      // 5. 원본 PDF URL 저장 (처음 서명할 때만)
      if (!originalPdfUrl) {
        setOriginalPdfUrl(pdfUrl)
      }

      // 6. 이전 URL 정리
      if (pdfUrl && pdfUrl !== originalPdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }

      // 7. 새로운 PDF Blob URL 생성하여 뷰어 업데이트
      const pdfBlob = new Blob([signedPdfBytes], {
        type: 'application/pdf',
      })
      const newBlobUrl = URL.createObjectURL(pdfBlob)

      setSignatureImage(signatureDataURL)
      setPdfUrl(newBlobUrl)
      setShowSignatureModal(false)
      message.success('서명이 PDF에 추가되었습니다.')
    } catch (error) {
      message.error('서명 추가 중 오류가 발생했습니다.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleFinalSubmit = async () => {
    if (!signatureImage) {
      message.warning('먼저 서명을 등록해주세요.')
      return
    }

    if (!pdfUrl) {
      message.error('PDF가 없습니다.')
      return
    }

    setIsProcessing(true)
    try {
      // 이미 서명이 삽입된 PDF를 가져오기
      const pdfBytes = await fetch(pdfUrl).then((res) => {
        if (!res.ok) {
          throw new Error('PDF를 불러올 수 없습니다.')
        }
        return res.arrayBuffer()
      })

      // S3에 업로드
      const contractId = contract?.id || contract?.key
      const effectiveUserId = userId || user?.id
      if (!effectiveUserId) {
        message.error('사용자 정보를 찾을 수 없습니다.')
        setIsProcessing(false)
        return
      }
      const s3Key = `contracts/signed/${effectiveUserId}/${contractId}_${Date.now()}`

      const pdfBlob = new Blob([pdfBytes], {
        type: 'application/pdf',
      })
      const pdfFile = new File(
        [pdfBlob],
        `signed-${contract?.contractName || 'contract'}.pdf`,
        { type: 'application/pdf' },
      )

      const uploadParams = {
        Key: `${s3Key}.pdf`,
        Body: pdfFile,
        ContentType: 'application/pdf',
      }

      const uploadResult = await salusS3.putObject(uploadParams).promise()

      if (!uploadResult) {
        throw new Error('서명된 계약서 업로드에 실패했습니다.')
      }

      // 업로드가 성공하면 S3 key만 백엔드로 전달하여 최종 서명 처리
      const res = await signContract(contractId, s3Key, {
        userId: effectiveUserId,
      })
      console.log('signContract res::', res)
      if (res.status === 200) {
        message.success('서명이 완료되었습니다.')
        onClose()
        // 서명 완료 콜백 호출 (계약서 목록 리로드)
        if (onSignComplete) {
          onSignComplete()
        }
      } else {
        message.error('서명 처리 중 오류가 발생했습니다.')
      }
    } catch (error) {
      console.error('서명 처리 중 오류:', error)
      message.error('서명 처리 중 오류가 발생했습니다.')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <>
      <AopsCommonModalLayout
        open={open}
        onClose={onClose}
        title={
          mode === 'view'
            ? contract?.contractType === 'SALARY'
              ? '연봉 계약서 보기'
              : '근로계약서 보기'
            : contract?.contractType === 'SALARY'
              ? '연봉 계약서 서명하기'
              : '근로계약서 서명하기'
        }
      >
        <div className="contract-sign-modal-content">
          {/* 라벨 영역 */}
          <div className="contract-viewer-label contract-viewer-label-column">
            <span className="contract-status-badge">
              {contract?.status || (mode === 'view' ? '완료' : '진행중')}
            </span>
            <span className="contract-title-text">
              {contract?.contractName ||
                (contract?.contractType === 'SALARY'
                  ? '연봉 계약서'
                  : '근로 계약서')}
            </span>
          </div>

          {/* 뷰어 영역 */}
          {isGeneratingPdf ? (
            <div className="contract-viewer-container">
              <div className="contract-viewer-loading">
                <Spin size="small" />
                <div className="contract-viewer-loading-text">
                  잠시만 기다려주세요
                </div>
              </div>
            </div>
          ) : pdfUrl ? (
            <div className="contract-viewer-container">
              <DocumentViewer pdfUrl={pdfUrl} />
            </div>
          ) : (
            <div className="contract-viewer-container">
              <div className="contract-viewer-error">
                <div>PDF를 불러올 수 없습니다.</div>
                <div className="contract-viewer-error-text">
                  PDF 생성에 실패했습니다. 다시 시도해주세요.
                </div>
              </div>
            </div>
          )}

          {mode === 'sign' && (
            <div className="contract-sign-modal-footer">
              {!isChief && (
                <>
                  {signatureImage ? (
                    <div className="contract-sign-modal-footer-actions">
                      {/* <Button onClick={handleGoToSign}>
                      <EditOutlined /> 다시 서명하기
                    </Button> */}
                      <Button
                        type="primary"
                        size="large"
                        onClick={handleFinalSubmit}
                        className="btn-go-to-sign"
                        loading={isProcessing}
                      >
                        최종 제출하기
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleGoToSign}
                      className="btn-go-to-sign"
                      disabled={isProcessing || isGeneratingPdf || !pdfUrl}
                    >
                      서명하러 가기
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </AopsCommonModalLayout>

      {mode === 'sign' && (
        <SignatureModal
          open={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSave={handleSignatureSave}
        />
      )}
    </>
  )
}

export default ContractSignModal
